import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, Card } from "@/components/page-shell";
import {
  DISEASE_CATEGORY_LABEL,
  EndNote,
  H2,
  MoreLinks,
  SourcesAndDisclaimer,
} from "@/components/guide-page";
// Value import used only by the component below, so the route splitter keeps it out
// of the eager chunk; the loader re-imports it dynamically for the JSON-LD.
import { diseasesForHub, type Disease } from "@/lib/diseases";
import { breadcrumbJsonLd } from "@/lib/jsonld";
import { absUrl, ogImageUrl } from "@/lib/site";
import { pageTitle } from "@/lib/seo";

/** Links a disease to its flagship route (canonicalPath) if it has one, else /diseases/$slug. */
function DiseaseLink({
  d,
  className,
  children,
}: {
  d: Disease;
  className?: string;
  children: React.ReactNode;
}) {
  if (d.canonicalPath) {
    return (
      <Link to={d.canonicalPath} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <Link to="/diseases/$disease" params={{ disease: d.slug }} className={className}>
      {children}
    </Link>
  );
}

const PATH = "/diseases";
const OG = ogImageUrl({
  title: "Tick-borne diseases",
  subtitle: "Symptoms, onset windows & which tick carries what",
  kind: "guide",
});

export const Route = createFileRoute("/diseases/")({
  // The lookup is a dynamic import: routeTree.gen.ts pulls every route's loader and
  // head into the client's entry chunk, so a top-level import here would put the
  // whole disease library on the home page's critical path.
  loader: async (): Promise<{ parts: Array<{ name: string; url: string }> }> => {
    const { diseasesForHub: hub } = await import("@/lib/diseases");
    return {
      parts: hub().map((d) => ({
        name: d.name,
        url: absUrl(d.canonicalPath ?? `/diseases/${d.slug}`),
      })),
    };
  },
  head: ({ loaderData }) => {
    const parts = loaderData?.parts ?? [];
    return {
      meta: [
        { title: pageTitle("Tick-Borne Diseases: Symptoms and Which Tick Carries Each") },
        {
          name: "description",
          content:
            "Symptoms, how soon they start, and which tick carries each tick-borne disease in the US, from alpha-gal syndrome and Lyme to ehrlichiosis, RMSF and Powassan.",
        },
        { property: "og:title", content: "Tick-borne diseases: symptoms, onset & vectors" },
        {
          property: "og:description",
          content: "Symptoms, onset windows, and which tick carries each tick-borne disease.",
        },
        { property: "og:url", content: absUrl(PATH) },
        { property: "og:image", content: OG },
        { name: "twitter:title", content: "Tick-borne diseases" },
        { name: "twitter:image", content: OG },
      ],
      links: [{ rel: "canonical", href: absUrl(PATH) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Tick-borne diseases",
            url: absUrl(PATH),
            hasPart: parts.map((p) => ({
              "@type": "MedicalWebPage",
              name: p.name,
              url: p.url,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Tick-borne diseases", path: PATH },
            ]),
          ),
        },
      ],
    };
  },
  component: DiseasesHub,
});

function DiseasesHub() {
  const diseases = diseasesForHub();
  const groups = [
    {
      id: "lone-star",
      label: "Carried by the Lone Star tick",
      items: diseases.filter((d) => d.transmittedByLoneStar),
    },
    {
      id: "other-ticks",
      label: "Carried by other ticks",
      items: diseases.filter((d) => !d.transmittedByLoneStar),
    },
  ].filter((g) => g.items.length > 0);

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Tick-borne diseases", path: PATH },
      ]}
      title="Tick-borne diseases, and the ticks that carry them"
      intro="The illnesses ticks can transmit: what they are, the symptoms to watch for, how soon symptoms appear, and which tick carries each. The Lone Star tick's own illnesses are listed first."
    >
      <Card>
        <h2 className={H2}>How soon symptoms start</h2>
        <p className="measure mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">
          Roughly how soon symptoms tend to appear after exposure, and the main tick that carries
          each illness. Ranges are approximate; each disease page lists its sources.
        </p>

        {/* Wide screens: a printed table. */}
        <table className="mt-6 hidden w-full border-collapse text-left md:table">
          <caption className="sr-only">
            Tick-borne diseases: typical onset of symptoms and the ticks that carry each
          </caption>
          <thead>
            <tr className="border-b border-rule/70">
              <th
                scope="col"
                className="label-caps w-[30%] pb-2 pr-6 font-semibold text-muted-foreground"
              >
                Disease
              </th>
              <th scope="col" className="label-caps pb-2 pr-6 font-semibold text-muted-foreground">
                Typical onset
              </th>
              <th
                scope="col"
                className="label-caps w-[24%] pb-2 font-semibold text-muted-foreground"
              >
                Carried by
              </th>
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g.id}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={3}
                  className="label-caps pb-1 pt-6 font-semibold text-rust-ink"
                >
                  {g.label}
                </th>
              </tr>
              {g.items.map((d) => (
                <tr key={d.slug} className="border-t border-border align-top">
                  <th scope="row" className="py-3.5 pr-6 font-normal">
                    <DiseaseLink
                      d={d}
                      className="font-display text-lg font-medium leading-snug text-foreground underline decoration-border underline-offset-4 transition-colors hover:text-primary hover:decoration-current"
                    >
                      {d.name}
                    </DiseaseLink>
                    {DISEASE_CATEGORY_LABEL[d.category] && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {DISEASE_CATEGORY_LABEL[d.category]}
                      </span>
                    )}
                  </th>
                  <td className="py-3.5 pr-6 text-sm leading-relaxed text-foreground/85">
                    {d.onsetWindow}
                  </td>
                  <td className="py-3.5 text-sm leading-relaxed text-foreground/85">
                    {d.vectorTicks.join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>

        {/* Phones: the same rows, stacked, so nothing scrolls sideways. */}
        <div className="mt-6 space-y-8 md:hidden">
          {groups.map((g) => (
            <section key={g.id} aria-labelledby={`m-${g.id}`}>
              <h3 id={`m-${g.id}`} className="label-caps text-rust-ink">
                {g.label}
              </h3>
              <ul className="mt-2 divide-y divide-border border-y border-border">
                {g.items.map((d) => (
                  <li key={d.slug} className="py-4">
                    <DiseaseLink
                      d={d}
                      className="font-display text-xl font-medium leading-snug text-foreground underline decoration-border underline-offset-4 hover:text-primary hover:decoration-current"
                    >
                      {d.name}
                    </DiseaseLink>
                    {DISEASE_CATEGORY_LABEL[d.category] && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {DISEASE_CATEGORY_LABEL[d.category]}
                      </span>
                    )}
                    <dl className="mt-2 space-y-2 text-sm leading-relaxed">
                      <div>
                        <dt className="label-caps text-muted-foreground">Typical onset</dt>
                        <dd className="mt-0.5 text-foreground/85">{d.onsetWindow}</dd>
                      </div>
                      <div>
                        <dt className="label-caps text-muted-foreground">Carried by</dt>
                        <dd className="mt-0.5 text-foreground/85">{d.vectorTicks.join(", ")}</dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Card>

      <MoreLinks id="disease-next" title="Related">
        <li>
          <Link to="/ticks" className="link">
            Identify the tick
          </Link>
        </li>
        <li>
          <Link to="/tick-bite-tool" className="link">
            What to do after a bite
          </Link>
        </li>
        <li>
          <Link to="/prevention" className="link">
            How to avoid bites
          </Link>
        </li>
      </MoreLinks>

      <EndNote>
        This page is here to help you learn. It is not medical advice. Only a doctor can diagnose a
        tick-borne illness. If someone has trouble breathing or a severe allergic reaction, call
        911. <SourcesAndDisclaimer />
      </EndNote>
    </PageShell>
  );
}
